import { z } from "zod";

// Primitive shapes shared by every contract. Conventions fixed by the architecture
// (docs/architecture/01-system-design.md §5, 04-delivery-plan.md S1): money in integer
// centavos, timestamps ISO-8601 UTC, UUID identifiers (v7 where the row is born), cursor
// pagination `{ items, next_cursor }`, RFC 9457 problem details for errors.

export const Uuid = z.uuid();
export type Uuid = z.infer<typeof Uuid>;

export const IsoDateTime = z.iso.datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

export const IsoDate = z.iso.date();
export type IsoDate = z.infer<typeof IsoDate>;

/** Integer amount in Mexican centavos. Never a float. */
export const Centavos = z.number().int().min(0).max(1_000_000_000);
export type Centavos = z.infer<typeof Centavos>;

/** Signed centavos (variances, adjustments). */
export const SignedCentavos = z.number().int().min(-1_000_000_000).max(1_000_000_000);

export const Sex = z.enum(["M", "F"]);
export type Sex = z.infer<typeof Sex>;

export const NonEmptyString = z.string().trim().min(1).max(500);
export const ShortText = z.string().trim().max(200);
export const LongText = z.string().trim().max(4000);

/** E.164-ish Mexican phone: 10 digits, optional +52 / 52 prefix. Stored normalised to 10 digits. */
export const MxPhone = z
  .string()
  .trim()
  .regex(/^(\+?52)?\s?\d{10}$/, "Teléfono de 10 dígitos")
  .transform((s) => s.replace(/\D/g, "").slice(-10));

export const Email = z.email().max(254);

/**
 * Boolean in a query string. `z.coerce.boolean()` turns the string "false" into true, so query
 * flags parse the literal words instead.
 */
export const QueryBool = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
  .transform((v) => v === true || v === "true" || v === "1");

export const PageQuery = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PageQuery = z.infer<typeof PageQuery>;

/**
 * Date filter of the work lists (Captura, Validación, Entrega, Solicitudes; Fernanda,
 * 2026-11-02): one lab day (`day`) or an inclusive range (`from`–`to`), every date YYYY-MM-DD in
 * America/Mexico_City. `from` alone means "from that day on", `to` alone "up to that day".
 * Spread into a query object and validate it with `checkDayRange`.
 */
export const DayRangeFields = {
  /** One day; default = every day. */
  day: IsoDate.optional(),
  /** First day of the range, inclusive. */
  from: IsoDate.optional(),
  /** Last day of the range, inclusive. */
  to: IsoDate.optional(),
};

/** `superRefine` for a query carrying `DayRangeFields`: `to` not before `from`, and `day` not mixed with a range. */
export function checkDayRange(q: { day?: string; from?: string; to?: string }, ctx: z.RefinementCtx): void {
  if (q.day && (q.from || q.to)) {
    ctx.addIssue({ code: "custom", path: ["day"], message: "Usa un día o un rango de fechas, no ambos." });
  }
  // ISO dates compare correctly as strings.
  if (q.from && q.to && q.to < q.from) {
    ctx.addIssue({ code: "custom", path: ["to"], message: "La fecha final no puede ser anterior a la inicial." });
  }
}

/**
 * A patch schema derived from a create schema: every field optional and, unlike `.partial()`,
 * with its `.default()` removed, so an omitted field stays `undefined` instead of parsing to the
 * default. A one-field `patient.update` must not null the phone and notes it did not mention.
 */
export function partialWithoutDefaults<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [key, ((field instanceof z.ZodDefault ? field.removeDefault() : field) as z.ZodTypeAny).optional()]),
  ) as { [K in keyof T]: z.ZodOptional<z.ZodType<z.output<T[K]>, z.input<T[K]>>> };
  return z.object(shape);
}

export function Page<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), next_cursor: z.string().nullable() });
}
export type Page<T> = { items: T[]; next_cursor: string | null };

/** RFC 9457 problem details. `type` is a stable URI per error; never a stack trace. */
export const Problem = z.object({
  type: z.string().url(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  /** Zod issues or domain-specific details, when useful to the client. */
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  /** `platform-login-disabled`: the laboratory whose staff tried the platform host, and where its staff sign in. */
  lab_display_name: z.string().optional(),
  primary_origin: z.string().url().optional(),
});
export type Problem = z.infer<typeof Problem>;

export const PROBLEM_BASE = "https://probetalab.com/problems/";
export const ProblemType = {
  validation: `${PROBLEM_BASE}validation`,
  unauthenticated: `${PROBLEM_BASE}unauthenticated`,
  forbidden: `${PROBLEM_BASE}forbidden`,
  not_found: `${PROBLEM_BASE}not-found`,
  conflict: `${PROBLEM_BASE}conflict`,
  invalid_credentials: `${PROBLEM_BASE}invalid-credentials`,
  /** A one-time credential link that is unknown, expired, already used or superseded. */
  invalid_token: `${PROBLEM_BASE}invalid-token`,
  command_rejected: `${PROBLEM_BASE}command-rejected`,
  unknown_command: `${PROBLEM_BASE}unknown-command`,
  /** The order has an unpaid balance: results are not delivered (meeting 2026-10-31). */
  balance_due: `${PROBLEM_BASE}balance-due`,
  /** A lifecycle precondition failed (item not validated, sample pending, …). */
  not_ready: `${PROBLEM_BASE}not-ready`,
  /** Too many requests from one client (login brute force, runaway device). */
  rate_limited: `${PROBLEM_BASE}rate-limited`,
  /**
   * A laboratory user signed in correctly on the platform host while its operator switch is off
   * (ADR 0025): no session; the problem carries `lab_display_name` and `primary_origin`.
   */
  platform_login_disabled: `${PROBLEM_BASE}platform-login-disabled`,
  /** The Host belongs to no laboratory and is not the platform host. */
  unknown_host: `${PROBLEM_BASE}unknown-host`,
  /** A state-changing request whose Origin is missing or is not this host (CSRF guard). */
  bad_origin: `${PROBLEM_BASE}bad-origin`,
  internal: `${PROBLEM_BASE}internal`,
} as const;
export type ProblemTypeKey = keyof typeof ProblemType;
